/**
 * REBUILDING A SEASON THAT WAS NEVER SAVED. D-EZ-66.
 *
 * Howard, 19 Aug 2026: *"so all of my season 1 stats cant be listed on the cards
 * as season #1 above the totals line? Its lost data?"*
 *
 * ===========================================================================
 * IT WAS NOT LOST, BECAUSE THE ENGINE IS SEEDED
 * ===========================================================================
 *
 * Before D-EZ-65 a slot carried the CARRY — the re-rated roster, the garage and
 * the seasons-run counter — and nothing else. So a league that rolled over
 * before that change has a live season and no history behind it, and the record
 * book, the career rows and the totals line have nothing to show.
 *
 * They can be got back. Every season this game has ever played is a pure
 * function of a seed and a starting roster, and the seeds form a chain:
 * `CareerScreen.run` starts season N on `base + N * 7919`, so from ANY season's
 * seed and number the whole chain backwards is arithmetic. Re-run it and the
 * same dice fall the same way.
 *
 * ⚠ THIS IS A RECOVERY AND NOT AN INVENTION, AND THE DIFFERENCE IS THE CHECK.
 * A rebuild that merely *looks* like the league would be the exact thing D-003
 * forbids — a plausible result nobody played. So nothing is filed until the
 * rebuilt chain's own winter is compared, field by field, against the carry the
 * save already holds. That carry was written by the real seasons. If the rebuilt
 * one matches it, the rebuild IS those seasons; if it does not, this module
 * refuses and says which field disagreed.
 *
 * MEASURED ON HOWARD'S OWN LEAGUE before a line of this was written — RASCAL,
 * live season 2 on seed 440080, so season 1 on 424242:
 *
 *   derived season-2 seed   440080   (saved: 440080)
 *   roster cards compared       53
 *   identical                   13
 *   differing                   40   — every one in `name` and nothing else
 *   missing                      0
 *
 * Gas, brake, qualities, starred, escape, fast pit, trophy, crew grade, team and
 * trait all came back identical POST-WINTER. Only the labels differed, because
 * the name pool is not in the seed. With the names put back the season-2
 * fingerprint came out `season-440080-s2-4e9f232028143b0e` — the same string the
 * saved row already carried.
 *
 * ===========================================================================
 * WHERE THE NAMES COME FROM, AND WHY THERE ARE TWO ANSWERS
 * ===========================================================================
 *
 * `buildField` names a field from its own vocabulary; `applyNamePool` then
 * substitutes whole AI-written names onto cards whose ratings were rolled before
 * any of that ran. The pool lives in `localStorage`, so it is NOT in the seed and
 * a rebuild has to get the names from somewhere.
 *
 *   FROM THE POOL — the good path. `namesForField` permutes the stored pool for
 *   a seed and cannot generate, extend or recombine a name, so the same pool and
 *   the same seed deal the same names. Nothing has to be trusted: the rebuilt
 *   names are then checked against the carry like everything else.
 *
 *   FROM THE SAVE — the fallback, when the pool is gone or has changed. The
 *   carry holds `driverId → name` for everybody who survived to the live season,
 *   and feeding those onto the starting field is the same substitution from a
 *   different source.
 *
 * ⚠ NAMES GO ON BEFORE THE SEASON RUNS, NEVER AFTER. Renaming a finished season
 * would leave every line of narration it already wrote holding the old name —
 * a season whose story and whose standings disagree about who did it. So the
 * substitution happens on the starting field and the season is run once.
 *
 * ⚠ THE FALLBACK CANNOT NAME A ROOKIE. A driver signed during one of the winters
 * being rebuilt did not exist when the field was dealt, so the save's map has no
 * hold on him and he keeps a built-in name. That is counted and reported rather
 * than hidden — see `RebuildOutcome.unnamed`.
 */

import { runCareerSeason, nextSeasonOptions, type CareerSeason } from './careerRunner.ts';
import { buildField, type Team } from '../domain/field.ts';
import { applyNamePool } from '../domain/driverGen.ts';
import { namesForField, type NamePool } from './nameStore.ts';
import { archiveSeason, restoreArchived } from './seasonArchive.ts';
import type { DriverCard } from '../domain/types.ts';

/**
 * ⚠ THE STRIDE, AND IT IS NOT ARBITRARY — it is copied from the one line that
 * decides it. `CareerScreen.run` calls
 * `nextSeasonOptions(current, seed + (current.seasonNumber + 1) * 7919)`, so the
 * season NUMBERED n runs on `base + n * 7919` for every n above 1, and season 1
 * runs on the base itself. If that line ever changes, this one must change with
 * it or a rebuild will quietly produce a different league — which is why the
 * check at the end of this file exists and why it compares rather than assumes.
 */
export const SEASON_SEED_STRIDE = 7919;

/** The seed season `n` of a league runs on. */
export function seedForSeason(baseSeed: number, seasonNumber: number): number {
  return seasonNumber <= 1 ? baseSeed : baseSeed + seasonNumber * SEASON_SEED_STRIDE;
}

/** The league's own seed, read back off any season's. */
export function baseSeedOf(seed: number, seasonNumber: number): number {
  return seasonNumber <= 1 ? seed : seed - seasonNumber * SEASON_SEED_STRIDE;
}

/** Exactly what a slot carries forward, and what a rebuild is checked against. */
export interface RebuildCarry {
  roster: readonly DriverCard[];
  teams: readonly Team[];
  /**
   * Null when the thing being checked against does not carry it — a settled
   * `CareerSeason` records the winter it PRODUCED, not the counter it started
   * from. Reported as unchecked rather than quietly passed.
   */
  seasonsRun: Record<string, number> | null;
}

export interface RebuildRequest {
  /** The season on the workbench. Seasons 1..this-1 are what get rebuilt. */
  liveSeasonNumber: number;
  /** That season's seed. The chain is derived backwards from it. */
  liveSeed: number;
  /** The carry that season began from — the thing the rebuild is judged against. */
  carry: RebuildCarry;
  /** The stored pool, when there is one. Null falls straight to the save's map. */
  pool?: NamePool | null;
  onProgress?: (stage: string, done: number, total: number) => void;
}

/** One thing that was compared, and how it came out. */
export interface RebuildCheck {
  what: string;
  ok: boolean;
  detail: string;
}

export type NameSource = 'pool' | 'save' | 'builtin';

export interface RebuildOutcome {
  ok: boolean;
  /** Oldest first, tapes already stripped. Empty when `ok` is false. */
  seasons: CareerSeason[];
  checks: RebuildCheck[];
  /** Where the names on the rebuilt cards came from. */
  nameSource: NameSource;
  /** Drivers the name source could not name. Empty on the pool path. */
  unnamed: string[];
  /** One sentence for the screen. Always set, on success and on refusal. */
  line: string;
}

// ---------------------------------------------------------------------------
// Comparing
// ---------------------------------------------------------------------------

/** Every field of a card except the label. */
function ratingsOf(card: DriverCard): string {
  const { name: _name, ...rest } = card as DriverCard & { name: string };
  return JSON.stringify(rest, Object.keys(rest).sort());
}

/**
 * ⚠ RATINGS AND NAMES ARE CHECKED SEPARATELY, ON PURPOSE.
 *
 * They fail for different reasons and they mean different things. A ratings
 * mismatch means the SEASON did not reproduce — the dice fell differently, the
 * chain is wrong, the stride changed — and nothing may be filed. A name mismatch
 * means the pool moved, which is a labelling problem with a second answer
 * available. Folding them into one boolean would turn a recoverable league into
 * a refusal, and it would report "the season did not reproduce" for a cause that
 * has nothing to do with the season.
 */
function compareCards(
  rebuilt: readonly DriverCard[],
  saved: readonly DriverCard[],
): { ratings: RebuildCheck; names: RebuildCheck } {
  const by = new Map(saved.map((c) => [c.driverId, c]));
  let sameRatings = 0;
  let sameNames = 0;
  const missing: string[] = [];
  const wrongRatings: string[] = [];
  const wrongNames: string[] = [];

  for (const card of rebuilt) {
    const was = by.get(card.driverId);
    if (!was) {
      missing.push(card.driverId);
      continue;
    }
    if (ratingsOf(card) === ratingsOf(was)) sameRatings += 1;
    else wrongRatings.push(card.driverId);
    if (card.name === was.name) sameNames += 1;
    else wrongNames.push(`${card.name} should be ${was.name}`);
  }

  const sized = rebuilt.length === saved.length;
  const ratingsOk = sized && missing.length === 0 && wrongRatings.length === 0;

  return {
    ratings: {
      what: 'the drivers, rated',
      ok: ratingsOk,
      detail: ratingsOk
        ? `all ${sameRatings} cards came back with the ratings the save already holds`
        : !sized
          ? `the rebuild ended with ${rebuilt.length} drivers and the save holds ${saved.length}`
          : missing.length > 0
            ? `${missing.length} driver${missing.length === 1 ? '' : 's'} in the rebuild are not in the save (${missing.slice(0, 3).join(', ')})`
            : `${wrongRatings.length} card${wrongRatings.length === 1 ? '' : 's'} came back rated differently (${wrongRatings.slice(0, 3).join(', ')})`,
    },
    names: {
      what: 'the names',
      ok: ratingsOk && wrongNames.length === 0,
      detail:
        wrongNames.length === 0
          ? `all ${sameNames} names match the save`
          : `${wrongNames.length} name${wrongNames.length === 1 ? '' : 's'} differ (${wrongNames.slice(0, 2).join('; ')})`,
    },
  };
}

function compareTeams(rebuilt: readonly Team[], saved: readonly Team[]): RebuildCheck {
  const key = (list: readonly Team[]): string =>
    JSON.stringify([...list].sort((a, b) => a.teamId.localeCompare(b.teamId)));
  const ok = key(rebuilt) === key(saved);
  return {
    what: 'the garage',
    ok,
    detail: ok
      ? `all ${rebuilt.length} shops came back the same, liveries included`
      : `the rebuilt garage has ${rebuilt.length} shops and does not match the ${saved.length} in the save`,
  };
}

function compareSeasonsRun(
  rebuilt: Record<string, number>,
  saved: Record<string, number> | null,
): RebuildCheck {
  if (saved === null) {
    // ⚠ NOT A PASS. It is reported as unchecked and it does not block, because
    // 53 matching cards and 14 matching shops are already an overwhelming case —
    // but a screen that showed this as a tick would be claiming a comparison
    // that never happened.
    return {
      what: 'the seasons run',
      ok: true,
      detail: 'not checked — this file does not record the counter the season started from',
    };
  }
  const norm = (r: Record<string, number>): string =>
    JSON.stringify(Object.fromEntries(Object.entries(r ?? {}).sort(([a], [b]) => a.localeCompare(b))));
  const ok = norm(rebuilt) === norm(saved);
  return {
    what: 'the seasons run',
    ok,
    detail: ok ? 'every driver has the same number of years behind him' : 'the years-run counters do not agree',
  };
}

// ---------------------------------------------------------------------------
// Naming the starting field
// ---------------------------------------------------------------------------

interface NamedField {
  roster: DriverCard[];
  teams: Team[];
  source: NameSource;
  unnamed: string[];
}

/**
 * Season 1's field, dealt from the base seed and named.
 *
 * `buildField` is called ONCE and the two naming sources are applied to the same
 * cards, so the ratings cannot differ between the paths — only the labels can.
 */
function startingField(
  baseSeed: number,
  size: number,
  pool: NamePool | null,
  fromSave: Map<string, string>,
  useSave: boolean,
): NamedField {
  const field = buildField(baseSeed, { size });
  const teams = field.teams as Team[];

  if (!useSave) {
    const { names, source } = namesForField(pool, baseSeed, field.drivers.length);
    const { drivers } = applyNamePool(field.drivers, names);
    return {
      roster: drivers.map((d) => d.card),
      teams,
      source: source === 'pool' ? 'pool' : 'builtin',
      unnamed: [],
    };
  }

  const unnamed: string[] = [];
  const roster = field.drivers.map((d) => {
    const name = fromSave.get(d.card.driverId);
    if (!name) {
      unnamed.push(d.card.driverId);
      return d.card;
    }
    return name === d.card.name ? d.card : { ...d.card, name };
  });
  return { roster, teams, source: fromSave.size > 0 ? 'save' : 'builtin', unnamed };
}

// ---------------------------------------------------------------------------
// The rebuild
// ---------------------------------------------------------------------------

/** Run the chain once, from a named starting field, and archive as it goes. */
async function runChain(
  baseSeed: number,
  upTo: number,
  start: NamedField,
  onProgress: RebuildRequest['onProgress'],
): Promise<{
  archived: CareerSeason[];
  carry: { roster: DriverCard[]; teams: Team[]; seasonsRun: Record<string, number> };
}> {
  const archived: CareerSeason[] = [];
  let previous: CareerSeason | null = null;

  for (let n = 1; n <= upTo; n++) {
    const options =
      previous === null
        ? { seed: baseSeed, roster: start.roster, teams: start.teams }
        : nextSeasonOptions(previous, seedForSeason(baseSeed, n));
    const season = await runCareerSeason({
      ...options,
      seasonNumber: n,
      onProgress: (stage, done, total) =>
        onProgress?.(`Season ${n} of ${upTo} — ${stage}`, (n - 1) * total + done, upTo * total),
    });
    /**
     * ⚠ ARCHIVED IMMEDIATELY, AND THAT IS A MEMORY DECISION. A settled season is
     * ~139 MB in the browser and its archived form is ~21 MB. Holding the full
     * ones for a chain of five would be most of a gigabyte in a tab that is also
     * running the next one. Only the archived copy is kept; `previous` holds the
     * full season for exactly one iteration, because the next winter reads it.
     *
     * ⚠ AND RESTORED, WHICH IS NOT BELT-AND-BRACES. `archiveSeason` drops the
     * per-race event slices and per-race standings because they are DERIVABLE,
     * and `restoreArchived` is what derives them again. A season handed over
     * archived-but-not-restored reads as a real season and quietly answers some
     * questions wrong — `startStreak` came back 0 instead of 1 in the first run
     * of this code, on a board that was otherwise identical. Round-tripping here
     * means what the rebuild hands back is exactly what a save and a reload
     * would hand back, which is the only version worth testing against.
     */
    archived.push(restoreArchived(archiveSeason(season)));
    previous = season;
  }

  const last = previous as CareerSeason;
  const next = nextSeasonOptions(last, seedForSeason(baseSeed, upTo + 1));
  return {
    archived,
    carry: { roster: next.roster ?? [], teams: next.teams ?? [], seasonsRun: next.seasonsRun ?? {} },
  };
}

/**
 * Rebuild every season behind the one on the workbench, and refuse unless the
 * rebuilt winter matches the carry the save already holds.
 *
 * ⚠ IT WRITES NOTHING. The caller decides what to do with a verified chain; this
 * function's whole contract is "here is the history, and here is why you may
 * believe it". Keeping the check and the write apart is what makes it safe to
 * run twice.
 */
export async function rebuildPriorSeasons(request: RebuildRequest): Promise<RebuildOutcome> {
  const { liveSeasonNumber, liveSeed, carry, pool = null, onProgress } = request;
  const upTo = liveSeasonNumber - 1;

  if (upTo < 1) {
    return {
      ok: false,
      seasons: [],
      checks: [],
      nameSource: 'builtin',
      unnamed: [],
      line: 'This is the first season of the league, so there is nothing behind it to rebuild.',
    };
  }
  if (!carry.roster.length) {
    return {
      ok: false,
      seasons: [],
      checks: [],
      nameSource: 'builtin',
      unnamed: [],
      line: 'This league has no carried roster in it, so there is nothing to check a rebuild against. Nothing was changed.',
    };
  }

  const baseSeed = baseSeedOf(liveSeed, liveSeasonNumber);
  const size = carry.roster.length;
  const fromSave = new Map(carry.roster.map((c) => [c.driverId, c.name]));

  // ---- the pool first, because it can name a rookie and the save cannot -----
  onProgress?.('Dealing the original field', 0, 1);
  let field = startingField(baseSeed, size, pool, fromSave, false);
  let run = await runChain(baseSeed, upTo, field, onProgress);
  let cards = compareCards(run.carry.roster, carry.roster);

  /**
   * ⚠ THE SECOND ATTEMPT IS ONLY EVER ABOUT NAMES. It is tried when the ratings
   * agree and the labels do not, which is exactly the pool-has-moved case, and
   * it is NOT tried when the ratings disagree — that is a broken chain and
   * renaming it would be dressing up a season nobody played.
   */
  if (cards.ratings.ok && !cards.names.ok) {
    onProgress?.('Re-dealing the names from the save', 0, 1);
    field = startingField(baseSeed, size, pool, fromSave, true);
    run = await runChain(baseSeed, upTo, field, onProgress);
    cards = compareCards(run.carry.roster, carry.roster);
  }

  const checks: RebuildCheck[] = [
    cards.ratings,
    cards.names,
    compareTeams(run.carry.teams, carry.teams),
    compareSeasonsRun(run.carry.seasonsRun, carry.seasonsRun),
  ];

  /**
   * ⚠ WHAT MAKES IT FILEABLE IS THE SEASON, NOT THE LABELS. A name that could
   * not be recovered is reported and does not block: the stats are the recovery,
   * and a card reading a built-in name over the right numbers is honest as long
   * as the screen says so. A rating, a shop or a years-run counter that does not
   * match means the chain is not this league, and that always refuses.
   */
  const structural = [cards.ratings, checks[2], checks[3]];
  const ok = structural.every((c) => c.ok);
  const failed = structural.find((c) => !c.ok);

  if (!ok) {
    return {
      ok: false,
      seasons: [],
      checks,
      nameSource: field.source,
      unnamed: field.unnamed,
      line:
        `The rebuild does not match this league and nothing was filed — ${failed?.detail}. ` +
        'That means the seasons behind this one were not played on the seeds this build derives, ' +
        'so re-running them would produce a league you never played.',
    };
  }

  /**
   * ⚠ THE DRIVERS THIS COULD NOT NAME ARE NAMED IN THE SENTENCE. On the fallback
   * path the save's map only holds men who SURVIVED to the live season, so
   * anyone who lost his licence in one of the rebuilt winters raced under a name
   * this has no way to recover and keeps a built-in one. His starts, his money
   * and his finishes are right; the label on them is not the one the player
   * saw. Leaving that out of the sentence would be a silent cap on a screen
   * whose entire claim is that it shows its working.
   */
  const lost = field.unnamed.length;
  const named = cards.names.ok
    ? field.source === 'pool'
      ? 'every name came back off your stored pool'
      : lost === 0
        ? 'every name came out of the save'
        : `the ${carry.roster.length} names still in the league came out of the save`
    : `${lost} driver${lost === 1 ? '' : 's'} kept a built-in name`;

  const caveat =
    lost > 0
      ? ` ${lost} driver${lost === 1 ? '' : 's'} who lost ${lost === 1 ? 'his' : 'their'} licence during ` +
        `${upTo === 1 ? 'that winter' : 'those winters'} ${lost === 1 ? 'is' : 'are'} shown under a ` +
        'built-in name, because a save only carries the men who are still in the league — ' +
        'their races, money and finishes are the real ones.'
      : '';

  return {
    ok: true,
    seasons: run.archived,
    checks,
    nameSource: field.source,
    unnamed: field.unnamed,
    line:
      `Season${upTo === 1 ? '' : 's'} ${upTo === 1 ? '1' : `1-${upTo}`} rebuilt and checked against ` +
      `the winter this save already carried: the drivers, the garage and the years run all match, and ${named}.` +
      caveat,
  };
}
